import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "candidateSelect",
    "competenciesContainer",
    "competenciesSection",
    "courseId",
    "ratingTool",
    "ratingToolLabel",
    "encounterKindSelect",
    "patientNumberField",
    "caseSelect",
    "demographicsInput",
    "callSummaryInput",
    "callSummaryLabel",
    "nonPatientActivityTypeField",
    "nonPatientActivityTypeSelect",
    "patientSafetySection",
    "dateOfCallInput",
    "overallCommentsInput",
    "learnerReflectionField",
    "grsSection",
    "miniCexSection",
  ];

  static values = {
    draftCompetencies: { type: Object, default: {} },
  };

  connect() {
    if (this.hasCandidateSelectTarget) {
      if (this.candidateSelectTarget.value) {
        this.loadCandidateData(this.candidateSelectTarget.value);
      }
    }
    this.applyEncounterKindUI();
    this.hydrateScoreDefinitions();

    const form = this.element.querySelector("form");
    if (form) {
      form.addEventListener("submit", this.validateBeforeSubmit.bind(this));
    }
  }

  async candidateChanged(event) {
    const candidateId = event.target.value;
    if (!candidateId) {
      this.clearFormState();
      return;
    }

    await this.loadCandidateData(candidateId);
  }

  encounterKindChanged() {
    this.applyEncounterKindUI();
  }

  async loadCandidateData(candidateId) {
    this.showLoading();

    try {
      const response = await fetch(
        `/direct_field_ratings/competencies_for_candidate?candidate_id=${candidateId}`,
        {
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
        }
      );
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        const body = await response.text();
        throw new Error(
          this.extractErrorMessage(body) || "Unexpected response while loading competencies."
        );
      }
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || "Could not load candidate data.");
      }

      if (this.hasCompetenciesContainerTarget) {
        this.competenciesContainerTarget.innerHTML = payload.html;
      }
      if (this.hasCourseIdTarget) {
        this.courseIdTarget.value = payload.course_id || "";
      }
      if (this.hasRatingToolTarget) {
        this.ratingToolTarget.value = payload.rating_tool || "";
      }
      this.toggleRatingToolSections(payload.rating_tool);
      this.applyEncounterKindUI();
      requestAnimationFrame(() => this.applyDraftCompetencies());
    } catch (error) {
      this.showError(error.message);
      if (this.hasRatingToolLabelTarget) {
        this.ratingToolLabelTarget.textContent = error.message;
      }
      this.toggleRatingToolSections(null);
      this.applyEncounterKindUI();
    }
  }

  showLoading() {
    if (!this.hasCompetenciesContainerTarget) {
      return;
    }
    this.competenciesContainerTarget.innerHTML = `
      <div class="text-center py-3">
        <div class="spinner-border text-primary" role="status"></div>
      </div>
    `;
  }

  showError(message) {
    if (!this.hasCompetenciesContainerTarget) {
      return;
    }
    this.competenciesContainerTarget.innerHTML = `
      <div class="alert alert-danger mb-0">${this.escapeHtml(message)}</div>
    `;
  }

  clearFormState() {
    if (this.hasCompetenciesContainerTarget) {
      this.competenciesContainerTarget.innerHTML = `
        <div class="alert alert-info mb-0">Select a candidate to load competencies.</div>
      `;
    }
    if (this.hasCourseIdTarget) {
      this.courseIdTarget.value = "";
    }
    if (this.hasRatingToolTarget) {
      this.ratingToolTarget.value = "";
    }
    this.toggleRatingToolSections(null);
    this.applyEncounterKindUI();
  }

  toggleRatingToolSections(toolName) {
    const normalized = (toolName || "").toString().toLowerCase();
    const isGrs = ["grs", "global rating scale", "global_rating_scale"].includes(normalized);
    const isMiniCex = ["mini-cex", "mini_cex", "minicex"].includes(normalized);

    if (this.hasGrsSectionTarget) {
      this.grsSectionTarget.style.display = isGrs ? "block" : "none";
    }
    if (this.hasMiniCexSectionTarget) {
      this.miniCexSectionTarget.style.display = isMiniCex ? "block" : "none";
    }
    if (this.hasRatingToolLabelTarget) {
      if (isGrs) {
        this.ratingToolLabelTarget.textContent = "GRS";
      } else if (isMiniCex) {
        this.ratingToolLabelTarget.textContent = "Mini-CEX";
      } else {
        this.ratingToolLabelTarget.textContent =
          "Select a candidate to load rating tool.";
      }
    }
  }

  selectedEncounterKind() {
    if (!this.hasEncounterKindSelectTarget) {
      return "patient_encounter";
    }
    const raw = (this.encounterKindSelectTarget.value || "").toString().trim().toLowerCase();
    if (raw === "non_patient_encounter") {
      return "non_patient_encounter";
    }
    if (["simulation", "general_feedback"].includes(raw)) {
      return "non_patient_encounter";
    }
    return "patient_encounter";
  }

  applyEncounterKindUI() {
    const kind = this.selectedEncounterKind();
    const isNonPatient = kind === "non_patient_encounter";

    if (this.hasPatientNumberFieldTarget) {
      this.patientNumberFieldTarget.style.display = isNonPatient ? "none" : "";
    }

    if (this.hasCaseSelectTarget) {
      const caseInput = this.caseSelectTarget.closest(".input");
      if (caseInput) {
        const caseLabel = caseInput.querySelector("label");
        if (caseLabel) {
          caseLabel.textContent = isNonPatient ? "Case" : "Case *";
        }
        if (isNonPatient) {
          this.caseSelectTarget.required = false;
          caseInput.classList.remove("required");
        } else {
          this.caseSelectTarget.required = true;
          caseInput.classList.add("required");
        }
      }
    }

    if (this.hasDemographicsInputTarget) {
      this.demographicsInputTarget.required = true;
      const container = this.demographicsInputTarget.closest(".input");
      if (container) {
        container.style.display = "";
      }
    }

    if (this.hasCallSummaryInputTarget) {
      this.callSummaryInputTarget.required = true;
      const container = this.callSummaryInputTarget.closest("div");
      if (container) {
        container.style.display = "";
      }
    }

    if (this.hasCallSummaryLabelTarget) {
      this.callSummaryLabelTarget.textContent = isNonPatient
        ? "Purpose for Rating *"
        : "Call Summary *";
    }

    if (this.hasLearnerReflectionFieldTarget) {
      this.learnerReflectionFieldTarget.style.display = "";
    }

    if (this.hasCompetenciesSectionTarget) {
      this.competenciesSectionTarget.style.display = isNonPatient ? "none" : "";
    }

    if (this.hasPatientSafetySectionTarget) {
      this.patientSafetySectionTarget.style.display = isNonPatient ? "none" : "";
    }

    if (isNonPatient) {
      this.clearPatientOnlyFieldValues();
    }

    if (this.hasNonPatientActivityTypeFieldTarget) {
      this.nonPatientActivityTypeFieldTarget.style.display = isNonPatient ? "" : "none";
    }

    if (this.hasNonPatientActivityTypeSelectTarget) {
      this.nonPatientActivityTypeSelectTarget.required = isNonPatient;
    }

    if (this.hasOverallCommentsInputTarget) {
      this.overallCommentsInputTarget.required = false;
    }

    this.toggleRatingToolSections(this.hasRatingToolTarget ? this.ratingToolTarget.value : "");
  }

  clearPatientOnlyFieldValues() {
    const patientNo = this.element.querySelector('input[name="direct_field_rating[patient_no]"]');
    if (patientNo) {
      patientNo.value = "";
    }

    ["identifiers_used", "hand_hygiene_infection_control", "sbarr_imst_ambo_handover"].forEach((field) => {
      const radios = this.element.querySelectorAll(
        `input[type="radio"][name="direct_field_rating[${field}]"]`
      );
      radios.forEach((radio) => {
        radio.checked = false;
      });
    });

    const competencyCheckboxes = this.element.querySelectorAll(
      'input[type="checkbox"][name*="[selected]"]'
    );
    competencyCheckboxes.forEach((checkbox) => {
      if (!checkbox.checked) {
        return;
      }
      checkbox.checked = false;
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  patientSafetyFieldLabels() {
    return {
      identifiers_used: "Two person-specific identifiers used?",
      hand_hygiene_infection_control: "Proper Hand Hygiene and Infection Control",
      sbarr_imst_ambo_handover: "SBARR or IMST-AMBO format used during patient hand-over",
    };
  }

  scoreChanged(event) {
    this.renderScoreDefinition(event.target);
  }

  renderScoreDefinition(radio) {
    const descriptionId = radio.dataset.descriptionId;
    const definition = radio.dataset.definition || "";
    const shortLabel = radio.dataset.shortLabel || "";
    if (!descriptionId) {
      return;
    }
    const descriptionEl = this.element.querySelector(`#${descriptionId}`);
    if (!descriptionEl) {
      return;
    }
    const shortLabelHtml = shortLabel
      ? `<strong>${this.escapeHtml(shortLabel)}</strong>`
      : "";
    const definitionHtml = definition
      ? ` ${this.escapeHtml(definition)}`
      : "";
    descriptionEl.innerHTML = `${shortLabelHtml}${definitionHtml}`.trim();
    descriptionEl.classList.remove("d-none");
  }

  hydrateScoreDefinitions() {
    const checkedRadios = this.element.querySelectorAll(
      "input[type='radio'][data-description-id]:checked"
    );
    checkedRadios.forEach((radio) => {
      this.renderScoreDefinition(radio);
    });
  }

  validateBeforeSubmit(event) {
    this.restoreSubmitButtons(event.target);

    const errors = [];
    const tool = this.hasRatingToolTarget ? this.ratingToolTarget.value : "";
    const normalized = (tool || "").toLowerCase();
    const isGrs = ["grs", "global rating scale", "global_rating_scale"].includes(normalized);
    const isMiniCex = ["mini-cex", "mini_cex", "minicex"].includes(normalized);
    const encounterKind = this.selectedEncounterKind();
    const isNonPatient = encounterKind === "non_patient_encounter";

    if (this.hasDateOfCallInputTarget && !this.dateOfCallInputTarget.value.trim()) {
      errors.push("Date of call is required.");
    }

    if (this.hasDemographicsInputTarget && !this.demographicsInputTarget.value.trim()) {
      errors.push("Demographics is required.");
    }

    if (this.hasCallSummaryInputTarget && !this.callSummaryInputTarget.value.trim()) {
      errors.push(
        isNonPatient ? "Purpose for rating is required." : "Call summary is required."
      );
    }

    if (isNonPatient && this.hasNonPatientActivityTypeSelectTarget) {
      const activityType = (this.nonPatientActivityTypeSelectTarget.value || "").trim();
      if (!activityType) {
        errors.push("Non-patient rating type is required.");
      }
    }

    if (!isNonPatient) {
      if (this.hasCaseSelectTarget && !this.caseSelectTarget.value) {
        errors.push("Case is required.");
      }
      const patientNoInput = this.element.querySelector(
        'input[name="direct_field_rating[patient_no]"]'
      );
      if (patientNoInput && !patientNoInput.value.trim()) {
        errors.push("Patient number is required.");
      }

      const safetyLabels = this.patientSafetyFieldLabels();
      Object.keys(safetyLabels).forEach((field) => {
        const radios = this.element.querySelectorAll(
          `input[type="radio"][name="direct_field_rating[${field}]"]`
        );
        if (radios.length > 0 && !Array.from(radios).some((radio) => radio.checked)) {
          errors.push(`${safetyLabels[field]} requires Yes, No, or N/A.`);
        }
      });
    }

    const grsDomainLabels = {
      sa: "Situation Awareness", hg: "History Gathering", pa: "Patient Assessment",
      dm: "Decision Making", ru: "Resource Utilization", co: "Communication", ps: "Procedural Skills"
    };
    const miniCexDomainLabels = {
      ca: "Clinical Abilities", prc: "Patient Related Communication",
      phq: "Professionalism", trq: "Teamwork", nts: "Non-Technical Skills"
    };

    if (isGrs) {
      const domains = Object.keys(grsDomainLabels);
      const missing = domains.filter((d) => {
        const radios = this.element.querySelectorAll(`input[type="radio"][name="direct_field_rating[${d}_value]"]`);
        return !Array.from(radios).some((r) => r.checked);
      });
      if (missing.length > 0) {
        const names = missing.map((d) => grsDomainLabels[d]).join(", ");
        errors.push(`GRS scores required for: ${names}`);
      }
    } else if (isMiniCex) {
      const domains = Object.keys(miniCexDomainLabels);
      const missing = domains.filter((d) => {
        const radios = this.element.querySelectorAll(`input[type="radio"][name="direct_field_rating[mini_cex_scores][${d}]"]`);
        return !Array.from(radios).some((r) => r.checked);
      });
      if (missing.length > 0) {
        const names = missing.map((d) => miniCexDomainLabels[d]).join(", ");
        errors.push(`Mini-CEX scores required for: ${names}`);
      }

      const rubricKeys = ["furtherLearningText", "meetExpectationsText", "exceededText"];
      const rubricMissing = domains.filter((domain) => {
        const hasText = rubricKeys.some((key) => {
          const field = this.element.querySelector(
            `textarea[name="direct_field_rating[mini_cex_rubrics][${domain}][${key}]"]`
          );
          return field && field.value.trim().length > 0;
        });
        return !hasText;
      });
      if (rubricMissing.length > 0) {
        const names = rubricMissing.map((d) => miniCexDomainLabels[d]).join(", ");
        errors.push(`Mini-CEX rubric text required for: ${names}`);
      }
    }

    if (!isNonPatient) {
      const checkedCompetencies = this.element.querySelectorAll(
        'input[type="checkbox"][name*="[selected]"]:checked'
      );
      if (
        checkedCompetencies.length < 1 ||
        checkedCompetencies.length > 7
      ) {
        errors.push("Select between 1 and 7 competencies for patient encounters.");
      }
      if (checkedCompetencies.length > 0) {
        let unrated = 0;
        checkedCompetencies.forEach((cb) => {
          const container = cb.closest("[data-controller='competency-selection']");
          if (container && !container.querySelector('input[type="radio"][name*="[rating_value]"]:checked')) {
            unrated++;
          }
        });
        if (unrated > 0) {
          errors.push(`${unrated} selected competenc${unrated === 1 ? "y needs a" : "ies need"} performance rating.`);
        }
      }
    }

    if (errors.length > 0) {
      event.preventDefault();
      // Prevent Rails UJS from scheduling a delayed disable for aborted submits.
      event.stopPropagation();
      let banner = this.element.querySelector(".dfr__validation-errors");
      if (!banner) {
        banner = document.createElement("div");
        banner.className = "dfr__validation-errors dfr__validation-errors--below-submit";
        const form = this.element.querySelector("form");
        const submitBar = form ? form.querySelector(".dfr__bar") : null;
        if (submitBar) {
          submitBar.insertAdjacentElement("afterend", banner);
        } else if (form) {
          form.prepend(banner);
        }
      }
      banner.innerHTML = `<strong>Please fix the following:</strong><ul>${errors.map((e) => `<li>${this.escapeHtml(e)}</li>`).join("")}</ul>`;
      // Run again on the next tick in case another handler toggled disabled.
      window.setTimeout(() => this.restoreSubmitButtons(event.target), 0);
      banner.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } else {
      const banner = this.element.querySelector(".dfr__validation-errors");
      if (banner) banner.remove();
    }
  }

  saveDraft(event) {
    const form = this.element.querySelector("form");
    if (!form) return;

    let draftInput = form.querySelector('input[name="save_as_draft"]');
    if (!draftInput) {
      draftInput = document.createElement("input");
      draftInput.type = "hidden";
      draftInput.name = "save_as_draft";
      form.appendChild(draftInput);
    }
    draftInput.value = "1";

    form.noValidate = true;

    const banner = this.element.querySelector(".dfr__validation-errors");
    if (banner) banner.remove();

    form.submit();
  }

  applyDraftCompetencies() {
    const competencies = this.draftCompetenciesValue;
    if (!competencies || Object.keys(competencies).length === 0) return;

    Object.values(competencies).forEach((comp) => {
      if (!comp.selected || comp.selected !== "1") return;
      const loId = comp.learning_objective_id;
      if (!loId) return;

      const checkbox = this.element.querySelector(
        `input[type="checkbox"][name*="[selected]"][data-learning-objective-id="${loId}"]`
      );
      if (!checkbox) return;

      checkbox.checked = true;
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));

      if (comp.rating_value) {
        const container = checkbox.closest("[data-controller='competency-selection']");
        if (container) {
          const radio = container.querySelector(
            `input[type="radio"][value="${comp.rating_value}"]`
          );
          if (radio) {
            radio.checked = true;
            radio.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
      }

      if (comp.comments) {
        const container = checkbox.closest("[data-controller='competency-selection']");
        if (container) {
          const textarea = container.querySelector("textarea");
          if (textarea) textarea.value = comp.comments;
        }
      }
    });
  }

  restoreSubmitButtons(form) {
    if (!form) return;

    const submitButtons = form.querySelectorAll(
      'button[type="submit"], input[type="submit"]'
    );

    submitButtons.forEach((button) => {
      button.disabled = false;
      button.classList.remove("disabled");

      const enableWith = button.getAttribute("data-ujs-enable-with");
      if (enableWith !== null) {
        if (button.tagName === "INPUT") {
          button.value = enableWith;
        } else {
          button.innerHTML = enableWith;
        }
        button.removeAttribute("data-ujs-enable-with");
      }
    });
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  extractErrorMessage(html) {
    if (!html) {
      return null;
    }
    if (html.includes("You need to sign in or sign up before continuing")) {
      return "Your session appears to have expired. Please sign in again.";
    }
    return null;
  }
}
